/**
 * Client upload helper backed by server-issued presigned S3 URLs.
 *
 * AWS credentials are NOT shipped in the browser bundle. Instead we ask the
 * server for a short-lived presigned POST (with a max content-length condition),
 * upload the file directly to S3, then hand the returned object key to the
 * listing mutation.
 */

export type UploadKind = "vehicle" | "title" | "profile" | "video";

type PresignUpload = {
  key: string;
  uploadUrl: string;
  contentType: string;
  /** Form fields for the S3 POST (policy + signature). Required for size-limited uploads. */
  fields?: Record<string, string>;
  method?: "POST" | "PUT";
  maxContentLength?: number;
};

type PresignResponse = {
  uploads: PresignUpload[];
  errors?: Array<{ message: string }>;
};

/** Derive a same-origin API path from the configured GraphQL endpoint. */
function apiEndpoint(path: string): string {
  const graphql =
    process.env.REACT_APP_GRAPHQL_ENDPOINT || "http://localhost:8081/graphql";
  const normalized = path.startsWith("/") ? path : `/${path}`;
  if (graphql.endsWith("/graphql")) {
    return graphql.slice(0, -"/graphql".length) + normalized;
  }
  return graphql.replace(/\/$/, "") + normalized;
}

function presignEndpoint(): string {
  return apiEndpoint("/uploads/presign");
}

function deleteEndpoint(): string {
  return apiEndpoint("/uploads/delete");
}

function titleVerifyEndpoint(): string {
  return apiEndpoint("/title/verify");
}

export type TitleVerifyStatus =
  | "idle"
  | "checking"
  | "passed"
  | "failed"
  | "unavailable";

export type TitleVerifyResult = {
  verified: boolean;
  message: string;
  code: string | null;
  signals: string[];
  extractedVin: string | null;
  titleLike: boolean;
  cached: boolean;
  status: TitleVerifyStatus;
};

function authHeader(): Record<string, string> {
  // Dual-mode: cookie via credentials include; Bearer when localStorage present.
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/** Normalize MIME so S3 policy `eq $Content-Type` matches the file part. */
function normalizeContentType(file: File, fallback?: string): string {
  const raw = (file.type || fallback || "").toLowerCase();
  if (raw === "image/jpg") return "image/jpeg";
  if (raw === "video/mp4" || raw === "video/webm") return raw;
  if (raw.startsWith("image/")) return raw;
  // Empty MIME + image/video-looking name (common on iOS camera roll).
  if (/\.(jpe?g)$/i.test(file.name)) return "image/jpeg";
  if (/\.png$/i.test(file.name)) return "image/png";
  if (/\.webp$/i.test(file.name)) return "image/webp";
  if (/\.(mp4|m4v)$/i.test(file.name)) return "video/mp4";
  if (/\.webm$/i.test(file.name)) return "video/webm";
  return fallback || "image/jpeg";
}

/** Pull `<Code>` / `<Message>` out of an S3 XML error body when present. */
async function s3FailureMessage(
  fileName: string,
  res: Response
): Promise<string> {
  const status = res.status;
  let detail = "";
  try {
    const text = await res.text();
    const code = text.match(/<Code>([^<]+)<\/Code>/i)?.[1];
    const message = text.match(/<Message>([^<]+)<\/Message>/i)?.[1];
    if (code === "EntityTooLarge") {
      return `"${fileName}" is too large for upload. Try a smaller photo (max 10MB) or video (max 100MB).`;
    }
    if (code || message) {
      detail = ` ${[code, message].filter(Boolean).join(": ")}`;
    }
  } catch {
    // ignore parse failures
  }
  return `Failed to upload ${fileName} to storage (HTTP ${status}).${detail}`;
}

/** Upload one file to S3 using the server-issued POST (or legacy PUT) target. */
async function putOrPostToS3(upload: PresignUpload, file: File): Promise<void> {
  const max = upload.maxContentLength || 10 * 1024 * 1024;
  if (file.size > max) {
    throw new Error(
      `"${file.name}" is too large for upload (${Math.ceil(file.size / (1024 * 1024))}MB; max ${Math.floor(max / (1024 * 1024))}MB).`
    );
  }

  if (upload.fields && (upload.method === "POST" || !upload.method)) {
    const form = new FormData();
    // Policy fields must come before the file body. Force Content-Type to the
    // value the policy was signed with (presign response), not a stale empty
    // MIME from the File object.
    for (const [name, value] of Object.entries(upload.fields)) {
      if (name.toLowerCase() === "content-type") {
        form.append(name, upload.contentType || value);
      } else {
        form.append(name, value);
      }
    }
    // Ensure the file part carries a real image MIME — empty type breaks some
    // browsers' multipart encoding vs the signed Content-Type field.
    const typed =
      file.type && file.type === upload.contentType
        ? file
        : new File([file], file.name, {
            type: upload.contentType || normalizeContentType(file),
          });
    form.append("file", typed);
    const postRes = await fetch(upload.uploadUrl, {
      method: "POST",
      body: form,
    });
    if (!postRes.ok) {
      throw new Error(await s3FailureMessage(file.name, postRes));
    }
    return;
  }

  // Legacy PUT fallback (older servers without POST fields).
  const putRes = await fetch(upload.uploadUrl, {
    method: "PUT",
    headers: { "Content-Type": upload.contentType },
    body: file,
  });
  if (!putRes.ok) {
    throw new Error(await s3FailureMessage(file.name, putRes));
  }
}

/**
 * Upload a batch of files of a single kind. Returns the S3 object keys in the
 * same order as the input files.
 *
 * `namespace` is an optional extra key segment (e.g. e2e run id) inserted
 * after the user-scoped prefix: `{kind}/{userId}/{namespace}/{uuid}-…`.
 */
export async function uploadFiles(
  files: File[],
  kind: UploadKind = "vehicle",
  namespace?: string
): Promise<string[]> {
  if (files.length === 0) return [];

  const presignRes = await fetch(presignEndpoint(), {
    method: "POST",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...authHeader(),
    },
    body: JSON.stringify({
      namespace,
      files: files.map((f) => ({
        fileName: f.name,
        contentType: normalizeContentType(f),
        kind,
      })),
    }),
  });

  const payload = (await presignRes.json().catch(() => ({}))) as PresignResponse;
  if (!presignRes.ok || !payload.uploads) {
    const message =
      payload.errors?.map((e) => e.message).join("; ") ||
      `Failed to get upload URL (HTTP ${presignRes.status})`;
    throw new Error(message);
  }
  if (payload.uploads.length !== files.length) {
    throw new Error("Upload URL count did not match file count.");
  }

  await Promise.all(
    payload.uploads.map((upload, i) => putOrPostToS3(upload, files[i]))
  );

  return payload.uploads.map((u) => u.key);
}

/** Upload a single file and return its S3 object key. */
export async function uploadFile(
  file: File,
  kind: UploadKind = "vehicle",
  namespace?: string
): Promise<string> {
  const [key] = await uploadFiles([file], kind, namespace);
  return key;
}

export type DeleteUploadsResult = {
  deleted: string[];
  skipped: string[];
  errors: Array<{ key: string; message: string }>;
};

/**
 * Best-effort delete of owned S3 objects (removed listing photos/videos).
 * Does not throw on partial failures — callers can ignore errors after save.
 */
export async function deleteUploads(keys: string[]): Promise<DeleteUploadsResult> {
  const unique = Array.from(
    new Set(keys.filter((k) => typeof k === "string" && k.trim()))
  );
  if (unique.length === 0) {
    return { deleted: [], skipped: [], errors: [] };
  }

  const res = await fetch(deleteEndpoint(), {
    method: "POST",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...authHeader(),
    },
    body: JSON.stringify({ keys: unique }),
  });

  const payload = (await res.json().catch(() => ({}))) as DeleteUploadsResult & {
    errors?: Array<{ message: string } | { key: string; message: string }>;
  };

  if (!res.ok) {
    const message =
      (payload.errors || [])
        .map((e) => ("message" in e ? e.message : ""))
        .filter(Boolean)
        .join("; ") || `Failed to delete uploads (HTTP ${res.status})`;
    // Soft-fail: listing row already updated; orphan cleanup can retry later.
    return {
      deleted: [],
      skipped: unique,
      errors: unique.map((key) => ({ key, message })),
    };
  }

  return {
    deleted: payload.deleted || [],
    skipped: payload.skipped || [],
    errors: Array.isArray(payload.errors)
      ? payload.errors
          .filter((e): e is { key: string; message: string } => "key" in e)
          .map((e) => ({ key: e.key, message: e.message }))
      : [],
  };
}

/**
 * Ask the server to OCR-check a title image already uploaded to S3.
 * Soft failures (not a title / VIN mismatch) resolve with verified:false.
 * Hard failures (auth, outage) throw.
 */
export async function verifyTitleDocument(opts: {
  titleImageKey: string;
  vin?: string;
}): Promise<TitleVerifyResult> {
  const res = await fetch(titleVerifyEndpoint(), {
    method: "POST",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...authHeader(),
    },
    body: JSON.stringify({
      titleImageKey: opts.titleImageKey,
      vin: opts.vin || "",
    }),
  });

  const payload = (await res.json().catch(() => ({}))) as {
    verified?: boolean;
    message?: string;
    code?: string | null;
    signals?: string[];
    extractedVin?: string | null;
    titleLike?: boolean;
    cached?: boolean;
    errors?: Array<{ message: string; extensions?: { code?: string } }>;
  };

  if (!res.ok) {
    const code = payload.errors?.[0]?.extensions?.code || null;
    const message =
      payload.errors?.map((e) => e.message).join("; ") ||
      `Title verification failed (HTTP ${res.status})`;

    // Treat Textract subscription/outage as a soft unavailable so the seller
    // can still publish without a badge.
    if (
      res.status === 503 ||
      code === "TITLE_VERIFY_UNAVAILABLE" ||
      code === "TITLE_VERIFY_NOT_CONFIGURED"
    ) {
      return {
        verified: false,
        message:
          message ||
          "Title verification is temporarily unavailable. You can continue without the Title on File badge.",
        code,
        signals: [],
        extractedVin: null,
        titleLike: false,
        cached: false,
        status: "unavailable",
      };
    }

    throw new Error(message);
  }

  const verified = !!payload.verified;
  return {
    verified,
    message:
      payload.message ||
      (verified
        ? "Title document verified."
        : "Title document could not be verified."),
    code: payload.code ?? null,
    signals: payload.signals || [],
    extractedVin: payload.extractedVin ?? null,
    titleLike: !!payload.titleLike,
    cached: !!payload.cached,
    status: verified ? "passed" : "failed",
  };
}
